// src/utils/totals.js
import { resolveAdditionalCostFlags } from "./pricingEngine";
import { resolveOtherServiceCustomerAmounts } from "./otherServicePricing";

export const toCents  = (n) => Math.round((Number(n) || 0) * 100);
export const fromCents = (c) => (c || 0) / 100;
const includeDecorationBasisCents = (row = {}, flags = {}) => {
  const costC = toCents(row?.NetCost ?? row?.netCost ?? row?.Cost ?? row?.cost);
  if (flags?.includeInPrice && flags?.costType === "fixed") {
    const sellC = toCents(row?.Retail ?? row?.retail ?? row?.ClientPrice ?? row?.clientPrice);
    if (sellC > 0) return sellC;
  }
  return costC;
};
const allocateByQuantityExact = (quantities = [], totalCents = 0) => {
  const safeTotal = Math.max(0, Number(totalCents) || 0);
  const q = quantities.map((n) => Math.max(0, Number(n) || 0));
  const totalUnits = q.reduce((sum, n) => sum + n, 0);
  if (!safeTotal || !totalUnits) return q.map(() => 0);

  const basePerUnit = Math.floor(safeTotal / totalUnits);
  let remainder = safeTotal - (basePerUnit * totalUnits);
  const allocated = q.map((qty) => qty * basePerUnit);

  for (let i = 0; i < q.length && remainder > 0; i += 1) {
    const extra = Math.min(remainder, q[i]);
    allocated[i] += extra;
    remainder -= extra;
  }
  return allocated;
};

const resolveSharedDecorationMeta = (dec = {}, ch = {}) => {
  const dd = dec?.DecoratorDetails || {};
  const sharedGroupKey = String(ch?.sharedGroupKey ?? dd?.sharedGroupKey ?? "").trim();
  const rawMode = String(ch?.sharedQuantityMode ?? dd?.sharedQuantityMode ?? "").trim().toLowerCase();
  const sharedQuantityMode = (rawMode === "pooled" || rawMode === "shared") ? "pooled" : "line";
  const sharedTotalQuantityRaw = Number(ch?.sharedTotalQuantity ?? dd?.sharedTotalQuantity);
  const sharedTotalQuantity = Number.isFinite(sharedTotalQuantityRaw) && sharedTotalQuantityRaw > 0
    ? sharedTotalQuantityRaw
    : null;
  return {
    sharedGroupKey,
    sharedQuantityMode,
    sharedTotalQuantity,
    isPooledShared: Boolean(sharedGroupKey) && sharedQuantityMode === "pooled",
  };
};

const reconcileLineCents = (targetTotalCents, lines = []) => {
  const safeTarget = Number(targetTotalCents) || 0;
  const prepared = Array.isArray(lines) ? lines.map((line) => ({ ...line })) : [];
  if (!prepared.length || safeTarget <= 0) return prepared;

  const eligibleIdx = [];
  let current = 0;
  prepared.forEach((line, idx) => {
    const valueCents = Number(line?.valueCents) || 0;
    prepared[idx].valueCents = valueCents;
    current += valueCents;
    if (line?.eligible) eligibleIdx.push(idx);
  });
  if (!eligibleIdx.length) return prepared;

  let drift = safeTarget - current;
  if (!drift) return prepared;

  let cursor = 0;
  let loopGuard = Math.abs(drift) * (eligibleIdx.length + 1);
  while (drift !== 0 && loopGuard > 0) {
    const idx = eligibleIdx[cursor % eligibleIdx.length];
    const line = prepared[idx];
    if (drift > 0) {
      line.valueCents += 1;
      drift -= 1;
    } else if (line.valueCents > 0) {
      line.valueCents -= 1;
      drift += 1;
    }
    cursor += 1;
    loopGuard -= 1;
  }

  return prepared;
};
  const pickNum = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const pickPct = (...vals) => {
    for (const v of vals) {
      if (v === 0 || v === '0') return 0;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null; // important so code knows “no saved margin”
  };
  const pickBool = (...vals) => {
    for (const v of vals) {
      if (v === true || v === false) return Boolean(v);
    }
    return false;
  };
  
/**
 * normalizePricing
 * - Keeps your existing signature + return shape.
 * - If a row is locked (IsLocked) or an explicit Retail is present, we TRUST that Retail.
 *   Changing NetCost later (e.g., for POs) will NOT change revenue or shown prices.
 * - If unlocked and margin is provided, compute retail from cost+margin (your original policy).
 */
export const normalizePricing = (p) => {
  const qty       = pickNum(p?.Quantity, p?.quantity);
  const netCost   = pickNum(p?.NetCost, p?.Cost, p?.netCost, p?.cost);
  const inMargin  = pickPct(p?.Margin, p?.margin);       // may be null
  const retailBase = pickNum(
    p?.Retail,
    p?.retail,
  );
  const clientRetail = pickNum(
    p?.ClientPrice,
    p?.clientPrice,
    p?.Price,
    p?.price
  );
  const hasExplicitRetail = (p?.Retail != null && p?.Retail !== "") ||
    (p?.retail != null && p?.retail !== "");
  const retailOnlyLocked = pickBool(p?.IsRetailLocked, p?.isRetailLocked);
  const resolvedRetail = retailOnlyLocked && hasExplicitRetail
    ? retailBase
    : (clientRetail > 0 && (retailBase <= 0 || Math.abs(retailBase - netCost) <= 0.01))
      ? clientRetail
      : (retailBase > 0 ? retailBase : clientRetail);
  const hasRetail = resolvedRetail > 0;
  const locked = pickBool(p?.IsLocked, p?.isLocked) || retailOnlyLocked;
  const canDeriveRetailFromMargin =
    !locked && inMargin != null && inMargin > 0 && inMargin < 100 && netCost > 0;

  let retail;
  if (canDeriveRetailFromMargin) {
    retail = +(netCost / (1 - inMargin / 100)).toFixed(2);
  } else if (locked || hasRetail) {
    retail = resolvedRetail;
  } else {
    const expectedRetail =
      inMargin != null && inMargin > 0 && inMargin < 100 && netCost > 0
        ? +(netCost / (1 - inMargin / 100)).toFixed(2)
        : resolvedRetail;

    retail = (inMargin != null && netCost > 0)
      ? expectedRetail
      : (resolvedRetail > 0 ? resolvedRetail : expectedRetail);
  }

  const marginPct = retail > 0 ? ((retail - netCost) / retail) * 100 : 0;

  return {
    qty,
    costC:   toCents(netCost),
    retailC: toCents(retail),
    marginPct,
  };
};


// Retail from target margin (basis points = margin * 100)
// policy: "ceil" protects margin, "floor" favors customer
export const retailFromMarginCents = (costCents, marginBps = 4000, policy = "ceil") => {
  const denom = 10000 - marginBps; // e.g. 10000 - 4000 = 6000
  if (denom <= 0) return 0;
  const raw = costCents * 10000;    // scale to avoid floats
  return policy === "ceil"
    ? Math.ceil(raw / denom)
    : Math.floor(raw / denom);
};

// Effective margin % (to two decimals) from integer cents sums
export const marginPct2 = (profitCents, revenueCents) => {
  if (!revenueCents) return 0;
  // compute to basis points, then to percent with 2 decimals
  const bps = Math.round((profitCents * 10000) / revenueCents); // integer division
  return bps / 100; // e.g., 4002 → 40.02%
};

export const isCustomerFacingPriceRow = (row = {}) => {
  const flags = resolveAdditionalCostFlags(row);
  return !flags.subtractFromMargin && !flags.includeInPrice;
};

export const isCustomerFacingVisibleLine = (row = {}) => {
  const flags = resolveAdditionalCostFlags(row);
  return !flags.subtractFromMargin && flags.displayToClient && !flags.includeInPrice;
};

export const normalizeProductTaxIds = (product = {}) => {
  const raw = Array.isArray(product?.taxIds) && product.taxIds.length
    ? product.taxIds
    : [product?.taxId ?? product?.TaxId ?? product?.Tax];
  const seen = new Set();
  return raw
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0)
    .filter((id) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
};

const normalizeRateDecimal = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? n / 100 : n;
};

export const resolveProductTaxes = (product = {}, order = {}) => {
  const ids = normalizeProductTaxIds(product);
  const taxPool = Array.isArray(order?.taxOptions) ? order.taxOptions : [];
  const pickRate = (row = {}) => normalizeRateDecimal(row?.rate ?? row?.Rate ?? row?.value);
  const legacyDetails = product?.taxDetails || {};
  const legacyId = Number(
    legacyDetails?.taxId ?? product?.taxId ?? product?.TaxId ?? product?.Tax
  ) || null;

  // An explicit product tax selection is authoritative, including a 0% / exempt
  // selection. Never fall through to an older legacy rate for a different tax ID.
  if (ids.length) {
    return ids.map((id) => {
      const match = taxPool.find(
        (t) => String(t?.id ?? t?.taxId ?? t?.TaxId ?? t?.code) === String(id)
      );
      const matchingLegacyDetails =
        legacyId != null && String(legacyId) === String(id) ? legacyDetails : null;
      const source = match || matchingLegacyDetails || {};
      return {
        taxId: id,
        name: source?.name ?? source?.label ?? source?.type ?? `Tax ${id}`,
        type: source?.type ?? null,
        rate: pickRate(source),
        countryCode: source?.countryCode ?? null,
        stateCode: source?.stateCode ?? null,
      };
    });
  }

  const legacyRate = pickRate(legacyDetails);
  if (legacyRate > 0) {
    return [{
      taxId: legacyId,
      name: legacyDetails?.name ?? legacyDetails?.type ?? 'Tax',
      type: legacyDetails?.type ?? null,
      rate: legacyRate,
      countryCode: legacyDetails?.countryCode ?? null,
      stateCode: legacyDetails?.stateCode ?? null,
    }];
  }
  return [];
};

const normalizeTaxSignature = (tax = {}) => ({
  taxId: tax?.taxId == null ? null : String(tax.taxId),
  rate: +normalizeRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value).toFixed(8),
});

export const isPersistedTaxBreakdownCurrent = (
  persistedBreakdown = [],
  resolvedTaxes = [],
  taxableAmount = null
) => {
  const currentTaxableAmount = Number(taxableAmount);
  const hasCurrentTaxableAmount = Number.isFinite(currentTaxableAmount);

  const persisted = (Array.isArray(persistedBreakdown) ? persistedBreakdown : [])
    .filter((tax) => (Number(tax?.taxAmount ?? tax?.amount) || 0) !== 0 || normalizeRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value) > 0)
    .map((tax) => ({
      ...normalizeTaxSignature(tax),
      taxableAmount: Number(tax?.taxableAmount),
      taxAmount: Number(tax?.taxAmount ?? tax?.amount),
    }))
    .sort((a, b) => `${a.taxId}:${a.rate}`.localeCompare(`${b.taxId}:${b.rate}`));

  const current = (Array.isArray(resolvedTaxes) ? resolvedTaxes : [])
    .filter((tax) => normalizeRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value) > 0)
    .map(normalizeTaxSignature)
    .sort((a, b) => `${a.taxId}:${a.rate}`.localeCompare(`${b.taxId}:${b.rate}`));

  if (persisted.length !== current.length) return false;
  return current.every((tax, index) => {
    const saved = persisted[index];
    if (tax.taxId !== saved?.taxId || tax.rate !== saved?.rate) return false;
    if (!hasCurrentTaxableAmount) return true;

    const expectedTaxAmount = +(currentTaxableAmount * tax.rate).toFixed(2);
    return (
      Number.isFinite(saved?.taxableAmount) &&
      Math.abs(saved.taxableAmount - currentTaxableAmount) < 0.005 &&
      Number.isFinite(saved?.taxAmount) &&
      Math.abs(saved.taxAmount - expectedTaxAmount) < 0.005
    );
  });
};

export const buildResolvedTaxBreakdown = (resolvedTaxes = [], taxableAmount = 0) => {
  const base = Number(taxableAmount) || 0;
  return (Array.isArray(resolvedTaxes) ? resolvedTaxes : [])
    .filter((tax) => normalizeRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value) > 0)
    .map((tax) => {
      const rate = normalizeRateDecimal(tax?.rate ?? tax?.Rate ?? tax?.value);
      return {
        taxId: tax?.taxId ?? null,
        name: tax?.name ?? tax?.type ?? "Tax",
        type: tax?.type ?? null,
        rate,
        countryCode: tax?.countryCode ?? null,
        stateCode: tax?.stateCode ?? null,
        taxableAmount: +base.toFixed(2),
        taxAmount: +(base * rate).toFixed(2),
      };
    });
};

/**
 * computeTotalsFromOrder
 * Admin/editor shape:
 * order: { Products: [...], OtherService: [...] }
 *
 * Lock-aware behavior:
 * - Revenue is always based on Retail (normalizePricing respects lock + explicit Retail).
 * - Booked margin dollars are computed from (retail − cost) per line.
 * - Booked margin % is computed from subtotal: margin / subtotal.
 * - NetCost changes while locked will change cost and therefore margin dollars/percent,
 *   while revenue remains based on locked/saved retail.
 */
export function computeTotalsFromOrder(order = {}) {
  const products = Array.isArray(order?.Products) ? order.Products : [];
  const others   = Array.isArray(order?.OtherService) ? order.OtherService : [];

  let revenueAllC = 0;  // ALL rows for Subtotal/Total
  let taxC = 0;
  let marginC = 0;

  // Products
  products.forEach((product) => {
    const resolvedTaxes = resolveProductTaxes(product, order);
    const productDecorations = Array.isArray(product?.decorations) ? product.decorations : [];

    const rawPricings = (Array.isArray(product?.pricings) && product.pricings.length)
      ? product.pricings
      : [{
          Quantity: product?.Quantity ?? 1,
          NetCost:  product?.NetCost ?? product?.Cost ?? 0,
          Retail:   product?.Retail ?? 0,
          Margin:   product?.Margin ?? 40,
          IsLocked: Boolean(product?.IsLocked),
        }];

    const rowQuantities = rawPricings.map((row) => Number(row?.Quantity ?? row?.quantity) || 0);
    const totalUnits = rowQuantities.reduce((sum, qty) => sum + qty, 0);
    let includedDecorationCostC = 0;
    const processedSharedIncludeKeys = new Set();
    productDecorations.forEach((dec) => {
      (dec?.Charge || []).forEach((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        if (!flags.includeInPrice) return;
        const sharedMeta = resolveSharedDecorationMeta(dec, ch);
        if (sharedMeta.isPooledShared) {
          const includeKey = `${sharedMeta.sharedGroupKey}|include|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
          if (processedSharedIncludeKeys.has(includeKey)) return;
          processedSharedIncludeKeys.add(includeKey);
        }
        const cC = includeDecorationBasisCents(ch, flags);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const sharedQty = sharedMeta.sharedTotalQuantity ?? totalUnits;
        const multiplier = flags.costType === "fixed"
          ? 1
          : (sharedMeta.isPooledShared
            ? sharedQty
            : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : totalUnits));
        includedDecorationCostC += cC * multiplier;
      });
    });
    const includedCostByPricingRow = allocateByQuantityExact(rowQuantities, includedDecorationCostC);

    // Price breaks (with include-mode reconciliation to remove rounding drift)
    const pricingLines = [];
    rawPricings.forEach((p, rowIndex) => {
      const n = normalizePricing(p);
      const marginBps = pickPct(p?.Margin, p?.margin);
      const isLocked = pickBool(p?.IsLocked, p?.isLocked) || pickBool(p?.IsRetailLocked, p?.isRetailLocked);
      const needsDerivedRetail = n.retailC <= 0 || Math.abs(n.retailC - n.costC) <= 1;
      const marginBpsInt = marginBps != null ? Math.round(marginBps * 100) : null;
      const allocatedIncludedC = includedCostByPricingRow[rowIndex] || 0;
      const hasMargin = marginBpsInt != null && marginBpsInt >= 0 && marginBpsInt < 10000;
      const derivedFromBaseCostC = hasMargin
        ? toCents(fromCents(n.costC) / (1 - (marginBpsInt / 10000)))
        : 0;
      const rowClientPriceC = toCents(p?.ClientPrice ?? p?.clientPrice ?? p?.Price ?? p?.price);
      const looksAutoDerivedFromBase =
        hasMargin &&
        (Math.abs(n.retailC - derivedFromBaseCostC) <= 1 || Math.abs(rowClientPriceC - derivedFromBaseCostC) <= 1);
      const shouldRepriceForInclude = allocatedIncludedC > 0 && !isLocked && hasMargin && (needsDerivedRetail || looksAutoDerivedFromBase);
      const effectiveCostTotalC = (n.costC * n.qty) + allocatedIncludedC;
      const effectiveCostPerUnit = n.qty > 0
        ? fromCents(effectiveCostTotalC) / n.qty
        : fromCents(n.costC);
      const effectiveRetailC = ((!isLocked && needsDerivedRetail && hasMargin && effectiveCostTotalC > 0) || (shouldRepriceForInclude && effectiveCostTotalC > 0))
        ? toCents(+(effectiveCostPerUnit / (1 - (marginBpsInt / 10000))).toFixed(2))
        : n.retailC;
      const extC = effectiveRetailC * n.qty;
      const exactExtC = extC;

      pricingLines.push({
        valueCents: extC,
        targetCents: exactExtC,
        eligible: Boolean(shouldRepriceForInclude && !isLocked),
        costTotalCents: effectiveCostTotalC,
      });
    });
    const includeRepriceActive = pricingLines.some((line) => line.eligible);
    const targetPricingTotalC = includeRepriceActive
      ? pricingLines.reduce((sum, line) => sum + (Number(line.targetCents) || 0), 0)
      : 0;
    const reconciledPricingLines = includeRepriceActive
      ? reconcileLineCents(targetPricingTotalC, pricingLines)
      : pricingLines;
    reconciledPricingLines.forEach((line) => {
      const extC = Number(line?.valueCents) || 0;
      const costTotalC = Number(line?.costTotalCents) || 0;
      revenueAllC += extC;
      resolvedTaxes.forEach((tax) => {
        taxC += Math.round(extC * (Number(tax?.rate) || 0));
      });
      marginC += extC - costTotalC;
    });

    // Decorations
    const processedSharedDisplayKeys = new Set();
    productDecorations.forEach((dec) => {
      (dec?.Charge || []).forEach((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        const sharedMeta = resolveSharedDecorationMeta(dec, ch);
        if (sharedMeta.isPooledShared) {
          const sharedModeKey = flags.includeInPrice ? "include" : (flags.subtractFromMargin ? "subtract" : "display");
          const displayKey = `${sharedMeta.sharedGroupKey}|${sharedModeKey}|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
          if (processedSharedDisplayKeys.has(displayKey)) return;
          processedSharedDisplayKeys.add(displayKey);
        }
        const cC   = toCents(ch?.NetCost ?? ch?.netCost);
        const quotedMarginPct = pickPct(ch?.Margin, ch?.margin);
        const marginBps = quotedMarginPct != null ? Math.round(quotedMarginPct * 100) : null;
        const givenRetailC = toCents(ch?.Retail ?? ch?.retail);
        const needsDerivedRetail = givenRetailC <= 0 || Math.abs(givenRetailC - cC) <= 1;
        const rC = (needsDerivedRetail && marginBps != null && marginBps >= 0 && marginBps < 10000 && cC > 0)
          ? retailFromMarginCents(cC, marginBps, "ceil")
          : givenRetailC;
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const sharedQty = sharedMeta.sharedTotalQuantity ?? totalUnits;
        const multiplier = flags.costType === "fixed"
          ? 1
          : (sharedMeta.isPooledShared
            ? sharedQty
            : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : totalUnits));
        const extC = rC * multiplier;
        const costTotalC = cC * multiplier;

        if (flags.includeInPrice) return;
        if (flags.subtractFromMargin) {
          marginC -= costTotalC;
          return;
        }

        revenueAllC += extC;
        resolvedTaxes.forEach((taxRow) => {
          taxC += Math.round(extC * (Number(taxRow?.rate) || 0));
        });

        marginC += extC - costTotalC;
      });
    });
  });

  // Other Services

  others.forEach((os) => {
    const q      = Number(os?.Quantity ?? os?.quantity) || 0;
    const rt     = Number(os?.Taxes ?? os?.taxes) || 0; // percent
    const rate   = rt / 100;

    // Prefer Total (tax-in) to split out tax; fallback to RetailPrice * Qty (pre-tax)
    const totalC = toCents(Number(os?.Total ?? os?.total));
    let preTaxC;
    const quotedMarginPct = pickPct(os?.Margin, os?.margin);
    const marginBps = quotedMarginPct != null ? Math.round(quotedMarginPct * 100) : null;
    const ucC = toCents(Number(os?.UnitCost ?? os?.unitCost));
    const retailValue = os?.RetailPrice ?? os?.retailPrice;
    const hasExplicitRetail = retailValue !== null && retailValue !== undefined && retailValue !== '';
    const givenRpC = toCents(Number(retailValue));
    const needsDerivedRetail = !hasExplicitRetail || (givenRpC > 0 && Math.abs(givenRpC - ucC) <= 1);
    const derivedRpC = (needsDerivedRetail && marginBps != null && marginBps >= 0 && marginBps < 10000 && ucC > 0)
      ? retailFromMarginCents(ucC, marginBps, "ceil")
      : null;
    if (hasExplicitRetail) {
      preTaxC = givenRpC * q;
    } else if (totalC > 0 && !derivedRpC) {
      preTaxC = rate ? Math.round(totalC / (1 + rate)) : totalC;
    } else {
      const rpC0 = derivedRpC ?? toCents(Number(os?.RetailPrice ?? os?.retailPrice)); // per-unit, pre-tax
      preTaxC = rpC0 * q;
    }

    // Always contribute to revenue + tax
    revenueAllC += preTaxC;
    taxC        += Math.round(preTaxC * rate);

    // Booked margin dollars are always based on revenue minus cost.
    const rpC = derivedRpC ?? toCents(Number(os?.RetailPrice ?? os?.retailPrice));
    const profitC = (rpC - ucC) * q;

    marginC += profitC;
  });

  const subtotalC = revenueAllC;
  const totalC    = revenueAllC + taxC;

  // Margin % is derived directly from subtotal and margin dollars:
  // (Subtotal - Costs) / Subtotal.
  const marginPct = subtotalC > 0
    ? +(((fromCents(marginC) / fromCents(subtotalC)) * 100).toFixed(2))
    : 0;

  return {
    subtotalCents: subtotalC,
    taxCents:      taxC,
    totalCents:    totalC,
    marginCents:   marginC,
    marginPct,
  };
}



/**
 * computeTotalsFromSO
 * Public sales-order shape:
 * so: { products: [...], otherService: [...] }
 *
 * Sales order already carries client-facing prices (cartDetails[].Price),
 * so we simply extend revenue/tax from that. Lock does not affect SO here
 * because we’re not recomputing line pricing—those prices are already the
 * client’s prices.
 */
export function computeTotalsFromSO(so = {}) {
  const products = Array.isArray(so?.products) ? so.products : [];
  const others = [so?.otherService, so?.OtherService, so?.otherServices, so?.OtherServices]
    .find(Array.isArray) || [];

  let revenueC = 0;
  let taxC     = 0;

  // Products
  products.forEach((p) => {
    const resolvedTaxes = Array.isArray(p?.taxes) && p.taxes.length
      ? p.taxes.map((t) => ({ rate: Number(t?.rate) || 0 })).filter((t) => t.rate > 0)
      : [{ rate: Number(p?.taxRate?.rate) || 0 }].filter((t) => t.rate > 0);
    const explicitTaxBreakdown = Array.isArray(p?.taxBreakdown)
      ? p.taxBreakdown
      : (Array.isArray(p?.totals?.taxBreakdown) ? p.totals.taxBreakdown : []);
    const revenueBeforeThisProductC = revenueC;
    const nonTaxable = p?.taxable === false || p?.isTaxable === false ||
      p?.taxDetails?.taxable === false ||
      ['exempt', 'non-taxable'].includes(String(p?.taxTreatment || '').trim().toLowerCase());
    // The revenue added below already includes bundled (Include in Price)
    // decorations exactly once. The whole taxable selling price is the GST
    // base, not just the visible charge rows.
    const finishProductTax = () => {
      if (explicitTaxBreakdown.length || nonTaxable) return;
      const customerRevenueC = Math.max(0, revenueC - revenueBeforeThisProductC);
      taxC += resolvedTaxes.reduce(
        (sum, tax) => sum + Math.round(customerRevenueC * (Number(tax?.rate) || 0)),
        0
      );
    };
    if (explicitTaxBreakdown.length) {
      taxC += explicitTaxBreakdown.reduce(
        (sum, row) => sum + toCents(row?.taxAmount ?? row?.amount),
        0,
      );
    }

    const explicitProductTotal = Number(p?.total ?? p?.Total);
    if (Number.isFinite(explicitProductTotal) && explicitProductTotal > 0) {
      const explicitC = toCents(explicitProductTotal);
      revenueC += explicitC;
      finishProductTax();
      return;
    }

    const cartRows = Array.isArray(p?.cartDetails) && p.cartDetails.length
      ? p.cartDetails
      : (Array.isArray(p?.pricings) ? p.pricings : []);

    const rowQuantities = cartRows.map((row) => Number(row?.Quantity ?? row?.quantity) || 0);
    const totalUnits = rowQuantities.reduce((sum, qty) => sum + qty, 0);

    // Include-in-price decorations are fused into the product run charges on SO rendering.
    // Mirror that fusion here so footer totals match line-item totals.
    const embeddedIncludeByRow = (() => {
      if (!rowQuantities.length || !totalUnits) return rowQuantities.map(() => 0);
      const processedIncludeKeys = new Set();
      const includeTotalCents = (p?.decorations || []).reduce((sum, decoItem) => {
        const charges = decoItem?.Charge || [];
        return sum + charges.reduce((acc, ch) => {
          const flags = resolveAdditionalCostFlags(ch);
          if (!flags.includeInPrice) return acc;

          const sharedMeta = resolveSharedDecorationMeta(decoItem, ch);
          if (sharedMeta.isPooledShared) {
            const dedupeKey = `${sharedMeta.sharedGroupKey}|include|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
            if (processedIncludeKeys.has(dedupeKey)) return acc;
            processedIncludeKeys.add(dedupeKey);
          }

          const retailC = toCents(ch?.Retail ?? ch?.retail);
          const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
          const sharedQty = sharedMeta.sharedTotalQuantity ?? totalUnits;
          const multiplier = flags.costType === "fixed"
            ? 1
            : (sharedMeta.isPooledShared
              ? sharedQty
              : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : totalUnits));
          return acc + (retailC * multiplier);
        }, 0);
      }, 0);

      return allocateByQuantityExact(rowQuantities, includeTotalCents);
    })();

    cartRows.forEach((row, rowIndex) => {
      const priceC = toCents(
        row?.Price ?? row?.price ?? row?.ClientPrice ?? row?.clientPrice ?? row?.Retail ?? row?.retail
      );
      const q = Number(row?.Quantity ?? row?.quantity) || 0;
      const embeddedC = Number(embeddedIncludeByRow?.[rowIndex]) || 0;
      const extC = (priceC * q) + embeddedC;
      revenueC += extC;
    });

    const productAdditionalCharges = Array.isArray(p?.productAdditionalCharges)
      ? p.productAdditionalCharges
      : [];
    productAdditionalCharges.forEach((ch) => {
      if (!isCustomerFacingPriceRow(ch)) return;
      const explicitTotalC = toCents(Number(ch?.Total ?? ch?.total));
      const qty = Number(ch?.Quantity ?? ch?.quantity) || 0;
      const retailC = toCents(Number(ch?.Retail ?? ch?.retail ?? ch?.ClientPrice ?? ch?.clientPrice));
      const extC = explicitTotalC > 0 ? explicitTotalC : (retailC * qty);
      revenueC += extC;
    });

    // decorations → each charge a line
    const processedSharedDisplayKeys = new Set();
    (p?.decorations || []).forEach(d => {
      (d?.Charge || []).forEach(ch => {
        const rC = toCents(ch?.Retail);
        if (!isCustomerFacingPriceRow(ch)) return;
        const flags = resolveAdditionalCostFlags(ch);
        const sharedMeta = resolveSharedDecorationMeta(d, ch);
        if (sharedMeta.isPooledShared) {
          const displayKey = `${sharedMeta.sharedGroupKey}|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
          if (processedSharedDisplayKeys.has(displayKey)) return;
          processedSharedDisplayKeys.add(displayKey);
        }
        const qRaw  = Number(ch?.Quantity ?? ch?.quantity);
        const sharedQty = sharedMeta.sharedTotalQuantity ?? 0;
        const q = flags.costType === "fixed"
          ? 1
          : (sharedMeta.isPooledShared
            ? sharedQty
            : (Number.isFinite(qRaw) && qRaw > 0 ? qRaw : 0));

        const extC = rC * q;
        revenueC += extC;
      });
    });

    // (optional) artworks: mirror whatever your editor does if needed
    finishProductTax();
  });

  // Use the same presence-based customer pricing as the rendered service row.
  // In particular, an explicit RetailPrice of 0 overrides a stale Total.
  others.forEach((os) => {
    if (!isCustomerFacingPriceRow(os)) return;
    const amounts = resolveOtherServiceCustomerAmounts(os);

    revenueC += toCents(amounts.subtotal);
    taxC     += toCents(amounts.tax);
  });

  return {
    subtotalCents: revenueC,
    taxCents:      taxC,
    totalCents:    revenueC + taxC,
  };
}
